import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAppContext } from '../../Context';
import { useTranslation } from '../../TranslationContext';

function Download() {
    const { psServer, submittedCode, galleryContainerRef, setIsLoading, startDownloadInd, setStartDownloadInd, startDownloadAll, setStartDownloadAll } = useAppContext();
    const { t } = useTranslation();
    
    const downloadCounter = useRef(null);
    const downloadPercentage = useRef(null);
    const circularProgressRef = useRef(null);
    const circularProgress2Ref = useRef(null);
    const circularProgress3Ref = useRef(null);
    
    const [downloadQueue, setDownloadQueue] = useState([]);
    const [activeDownloads, setActiveDownloads] = useState([]);
    const [totalDownloadedBytes, setTotalDownloadedBytes] = useState(0);
    const [totalBytesToDownload, setTotalBytesToDownload] = useState(0);
    const [totalProgress, setTotalProgress] = useState(0);
    const [showProgress, setShowProgress] = useState(false);
    
    let tempTotalFiles = 0;
    
    const VAR_INNER = 'var(--innerCircle-progress)';
    const VAR_MIDDLE = 'var(--middleCircle-progress)';
    const VAR_OUTER = 'var(--outerCircle-progress)';
    const VAR_NONINNER = 'var(--innerCircle-nonProgress)';
    const VAR_NONMIDDLE = 'var(--middleCircle-nonProgress)';
    const VAR_NONOUTER = 'var(--outerCircle-nonProgress)';
    const initialStyle = {
        circularProgress: {
            background: `conic-gradient(${VAR_INNER} 0deg, ${VAR_NONINNER} 0deg)`
        },
        circularProgress2: {
            background: `conic-gradient(${VAR_MIDDLE} 0deg, ${VAR_NONMIDDLE} 0deg)`
        },
        circularProgress3: {
            background: `conic-gradient(${VAR_OUTER} 0deg, ${VAR_NONOUTER} 0deg)`
        }
    };
    
    useEffect(() => {
        if (startDownloadInd) {
            downloadIndImage();
        }
        if (startDownloadAll) {
            downloadAll();
        }
    }, [startDownloadInd, startDownloadAll]);
    
    if (!startDownloadAll) return null;

    function downloadAll() {
        setIsLoading(true);
        const MAX_CONCURRENT_DOWNLOADS = 5;
        const DOWNLOAD_DELAY = 1350;
        const galleryContainer = galleryContainerRef.current;
        if (galleryContainer) {
            const images = galleryContainer.querySelectorAll('img.download');
            const videos = galleryContainer.querySelectorAll('video.download');
            const srcs = [];
            const fileNames = [];
            Array.from(images).forEach(img => {
                let src = img.src;
                const modifiedSrc = src.replace('/media/thumb/', '/media/download/');
                srcs.push(modifiedSrc);
                const url = new URL(src);
                const pathname = url.pathname;
                const parts = pathname.split('/');
                const fileName = parts[parts.length - 2];
                fileNames.push(fileName);
            });
            Array.from(videos).forEach((video) => {
                const source = video.querySelector('source');
                if (source) {
                    const videoSrc = source.src;
                    const modifiedSrc = videoSrc.replace('/media/thumb/', '/media/download/');
                    srcs.push(modifiedSrc);
                    const url = new URL(modifiedSrc);
                    const pathname = url.pathname;
                    const parts = pathname.split('/');
                    const fileName = parts[parts.length - 2];
                    fileNames.push(fileName);
                }
            });
            if (srcs.length > 0) {
                tempTotalFiles = srcs.length;
                let totalDownloadedBytes = 0;
                let totalBytesToDownload = 0;

                const calculateTotalBytes = async () => {
                    try {
                        const response = await fetch(`${psServer}/media/download/checksize`, {
                            method: 'POST',
                            headers: {'Content-Type': 'application/json'},
                            body: JSON.stringify({ fileNames: fileNames, softCopy: submittedCode })
                        });
                        const data = await response.json();
                        totalBytesToDownload = parseInt(data['totalSize']);
                        
                    } catch (error) {
                        console.error('Error calculating total bytes:', error);
                    }    
                };
                
                calculateTotalBytes().then(() => {
                    if (downloadCounter.current) {
                        downloadCounter.current.innerHTML = `${t('downloaded')} 0/${srcs.length}`;
                    }
                    if (downloadPercentage.current) {
                        downloadPercentage.current.textContent = '0%';
                    }
                    
                    const downloadQueue = srcs.slice();
                    let completedDownloads = 0;
                    
                    setIsLoading(false);
                    setShowProgress(true);

                    async function processQueue() {
                        while (downloadQueue.length > 0) {
                            const activeDownloads = [];
                            for (let i = 0; i < MAX_CONCURRENT_DOWNLOADS && downloadQueue.length > 0; i++) {
                                const src = downloadQueue.shift();
                                await new Promise(resolve => setTimeout(resolve, DOWNLOAD_DELAY));
                                activeDownloads.push(downloadImage(src));
                            }
                            await Promise.allSettled(activeDownloads);
                        }
                        progressFadeOut();
                    }

                    async function downloadImage(src) {
                        return new Promise((resolve, reject) => {
                            const xhr = new XMLHttpRequest();
                            xhr.open('GET', src);
                            xhr.responseType = 'blob';
                            let imageDownloadedBytes = 0;

                            xhr.onprogress = function (event) {
                                imageDownloadedBytes = event.loaded;
                                let imageTotalBytes = parseInt(xhr.getResponseHeader('Content-Length')); 
                                if (!imageTotalBytes || imageTotalBytes === 0) {
                                    imageTotalBytes = parseInt(xhr.getResponseHeader('X-Content-Length'));
                                }
                                
                                const tempTotalProgress = Math.round(((totalDownloadedBytes + imageDownloadedBytes) / totalBytesToDownload) * 100);
                                if (tempTotalProgress > totalProgress) {
                                    setTotalProgress(tempTotalProgress);
                                    updateProgressBar(tempTotalProgress);
                                }
                            };

                            xhr.onload = function () {
                                if (xhr.status === 200) {
                                    totalDownloadedBytes += imageDownloadedBytes;
                                    const url = URL.createObjectURL(xhr.response);
                                    const a = document.createElement('a');
                                    const parts = src.split('/');
                                    const fileName = parts[parts.length - 2];
                                    a.href = url;
                                    a.download = fileName;
                                    a.style.display = 'none';
                                    document.body.appendChild(a);
                                    a.click();
                                    URL.revokeObjectURL(url);
                                    completedDownloads++;
                                    if (downloadCounter.current) {
                                        downloadCounter.current.innerHTML = `${t('downloaded')} ${completedDownloads}/${srcs.length}`;
                                    }
                                    resolve();
                                } else {
                                    reject(new Error(`Image download failed with status ${xhr.status}`));
                                    progressFadeOut();
                                }
                            };

                            xhr.onerror = function (error) {
                                reject(new Error('Network error during image download'));
                                progressFadeOut(); 
                                console.error('Image download failed:', error);
                                console.error('src is:', src);
                            };
                            xhr.send();
                        });
                    }
                    
                    function updateProgressBar(progress) {
                        if (downloadPercentage.current) {
                            downloadPercentage.current.textContent = `${progress}%`;
                        }
                        if (circularProgressRef.current && circularProgress2Ref.current && circularProgress3Ref.current) {
                            circularProgressRef.current.style.background = `conic-gradient(${VAR_INNER} ${progress * 3.6}deg, ${VAR_NONINNER} 0deg)`;
                            circularProgress2Ref.current.style.background = `conic-gradient(${VAR_MIDDLE} ${progress * 3.6}deg, ${VAR_NONMIDDLE} 0deg)`;
                            circularProgress3Ref.current.style.background = `conic-gradient(${VAR_OUTER} ${progress * 3.6}deg, ${VAR_NONOUTER} 0deg)`;
                        }
                    }
                    
                    function progressFadeOut() {
                        setTimeout(() => {
                            updateProgressBar(0); 
                            setDownloadQueue([]);
                            setActiveDownloads([]);
                            setTotalDownloadedBytes(0);
                            setTotalBytesToDownload(0);
                            setTotalProgress(0);
                            setShowProgress(false);
                            setStartDownloadAll(false);
                        }, 1000);
                    }
                    processQueue();
                });
            }
        } else {
            console.log("Can't see the gallery");
        }
    }

    function downloadIndImage() {
        const url = new URL(window.location.origin + startDownloadInd);
        //const url = new URL(startDownloadInd);
        const modifiedSrc = startDownloadInd.replace('/media/file/', '/media/download/');
        const pathname = url.pathname; 
        const parts = pathname.split('/');
        const fileName = parts[parts.length - 2];
        const link = document.createElement('a');
        link.href = modifiedSrc;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setStartDownloadInd('');
    }
    
    return (
        <>
            {showProgress && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                    className="progressWrap"
                >
                    <div className="circleProgress">
                        <div 
                            className="circularProgress3" 
                            ref={circularProgress3Ref} 
                            style={initialStyle.circularProgress3}
                        >
                            <div 
                                className="circularProgress2" 
                                ref={circularProgress2Ref} 
                                style={initialStyle.circularProgress2}
                            >
                                <div 
                                    className="circularProgress" 
                                    ref={circularProgressRef} 
                                    style={initialStyle.circularProgress}
                                >
                                    <div className="innerCircle"></div>
                                    <p className="percentage" ref={downloadPercentage}>0%</p>
                                </div>
                            </div>
                        </div>
                        <div className="progressCount" ref={downloadCounter}>
                            loading...
                        </div> 
                    </div>
                </motion.div>
            )}
        </>
    );
}

export default Download;